import { useState } from 'react';
import { doc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { AlertTriangle, X } from 'lucide-react';

interface Client {
  id: string;
  email: string;
  displayName?: string;
  companyId?: string;
}

interface DeleteClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  onDeleted: () => void;
}

export default function DeleteClientModal({
  isOpen,
  onClose,
  client,
  onDeleted
}: DeleteClientModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      // Check if client has any orders
      const ordersQuery = query(
        collection(db, 'orders'),
        where('buyerId', '==', client.id)
      );
      const ordersSnapshot = await getDocs(ordersQuery);

      if (!ordersSnapshot.empty) {
        setError('Não é possível excluir este cliente pois existem encomendas associadas');
        setLoading(false);
        return;
      }

      // Delete wallet if exists
      if (client.companyId) {
        const walletDoc = await getDocs(
          query(collection(db, 'wallets'), where('companyId', '==', client.companyId))
        );
        if (!walletDoc.empty) {
          await deleteDoc(doc(db, 'wallets', walletDoc.docs[0].id));
        }
      }

      // Delete user
      await deleteDoc(doc(db, 'users', client.id));

      onDeleted();
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Erro ao excluir cliente');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 text-red-600">
            <AlertTriangle size={24} />
            <h3 className="text-lg font-semibold">Remover Cliente</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 text-red-600 text-sm">
            {error}
          </div>
        )}

        <div className="mb-6">
          <p className="text-gray-700 mb-4">
            Tem certeza que deseja remover o cliente <strong>{client.displayName || client.email}</strong>?
          </p>
          <p className="text-red-600 text-sm">
            Esta ação não pode ser desfeita e removerá todos os dados do cliente, incluindo carteira.
          </p>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Removendo...' : 'Confirmar Remoção'}
          </button>
        </div>
      </div>
    </div>
  );
}